import React, { useState, useCallback, useEffect } from 'react';
import { propOr, path, isEmpty, equals, pathOr, compose, reverse, sortBy, prop, groupBy } from 'ramda';
import SanitizedHTML from 'react-sanitized-html';
import moment from 'moment';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { Editor } from '@tinymce/tinymce-react';
import { categories } from './JobSetup';
import Footer from './Footer';
import { overallRatingButtons, getOverallRatingKey, statusBadgeColors } from './Candidate';
import {
  EmailMajorMonotone,
  PhoneMajorMonotone,
  RefreshMajorMonotone,
  DuplicateMinor,
  EditMinor,
  ViewMinor,
  PromoteMinor,
  LinkMinor,
} from '@shopify/polaris-icons';
import { GET_JOB, GET_CURRENT_SUBSCRIPTION, GET_EMAIL_TEMPLATES } from '../apollo/queries';
import {
  UPDATE_JOB_STATUS,
  UPDATE_CANDIDATE_STATUS,
  CREATE_CANDIDATE_SCORE,
  UPDATE_CANDIDATE_SCORE,
  REFRESH_JOB,
  REFRESH_JOB_KIT_SKILL,
  SEND_CANDIDATE_EMAIL,
  ARCHIVE_JOB,
  UNARCHIVE_JOB,
} from '../apollo/mutation';
import {
  Page,
  Card,
  SkeletonBodyText,
  Button,
  ButtonGroup,
  Toast,
  AppProvider,
  Frame,
  TextStyle,
  Heading,
  Subheading,
  Icon,
  Layout,
  Badge,
  Link,
  Stack,
  Modal,
  TextContainer,
  EmptyState,
  Tabs,
  FormLayout,
  TextField,
  Checkbox,
  DropZone,
  Popover,
  ActionList,
  RadioButton,
  Select,
} from '@shopify/polaris';
import Loader from 'react-loader-spinner';
import { noJobsFoundImgUrl } from '../../utils/noJobsFoundImgUrl';
import { facebookIcon, twitterIcon, linkedinIcon } from '../../utils/socialIcons';

const facebookAppId = '1217389581968861';
const tabs = [
  {
    id: 'new_candidate',
    content: 'New',
    isList: true,
  },
  {
    id: 'active',
    content: 'Active',
    isList: true,
  },
  {
    id: 'hired',
    content: 'Hired',
    isList: true,
  },
  {
    id: 'inactive',
    content: 'Inactive',
    isList: true,
  },
  {
    id: 'job-description',
    content: 'Job Description',
    isList: false,
  },
  {
    id: 'application-form',
    content: 'Application Form',
    isList: false,
  },
];

const SocialIcon = ({ source, small = null }) => {
  const style = !!small ? { width: '20px', marginRight: '10px' } : {};
  return typeof source === 'string' ? (
    <img className='Polaris-Icon__Img' style={style} src={source} alt='' aria-hidden='true' />
  ) : (
    <div style={style}>
      <Icon source={source} />
    </div>
  );
};

const SOURCE_OPTIONS = [
  { label: 'Adzuna', value: 'Adzuna' },
  { label: 'AngelList', value: 'AngelList' },
  { label: 'AppCast', value: 'AppCast' },
  { label: 'College Recruiter', value: 'CollegeRecruiter' },
  { label: 'Craigslist', value: 'Craigslist' },
  { label: 'Dice', value: 'Dice' },
  { label: 'Dribble', value: 'Dribble' },
  { label: 'Flex Jobs', value: 'FlexJobs' },
  { label: 'The Hustle/Trends', value: 'Trends' },
  { label: 'Indeed Free', value: 'IndeedFree' },
  { label: 'Indeed Paid', value: 'IndeedPaid' },
  { label: 'Jobcase Free', value: 'JobcaseFree' },
  { label: 'Jobcase Paid', value: 'JobcasePaid' },
  { label: 'Linkedin Free', value: 'LinkedinFree' },
  { label: 'Linkedin Paid', value: 'LinkedinPaid' },
  { label: 'Nuevoo Free', value: 'NuevooFree' },
  { label: 'Nuevoo Paid', value: 'NuevooPaid' },
  { label: 'Product Hunt', value: 'ProductHunt' },
  { label: 'Seek', value: 'Seek' },
  { label: 'Shopify Community Job Board', value: 'ShopifyJobBoard' },
  { label: 'Snag', value: 'Snag' },
  { label: 'WeWorkRemotely', value: 'WeWorkRemotely' },
  { label: 'ZipRecruiter Free', value: 'ZipRecrtuiterFree' },
  { label: 'ZipRecruiter Paid', value: 'ZipRecrtuiterPaid' },
];

const JobCandidates = (props) => {
  const node = document.getElementsByClassName('shop_container');
  const dataProps = JSON.parse(node[0].getAttribute('data-react-props'));
  const shopifyToken = dataProps.shop.shopify_token;
  const changeRoute = path(['history', 'push'])(props);
  const initialJobId = props.match.params.id;
  const [currentJobId, setCurrentJobId] = useState(initialJobId);
  const { shopifyDomain, prymaryUrl } = { ...props };
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, {
    variables: { shopifyToken },
  });
  const { data: jobData, loading: jobLoading, refetch: jobRefetch } = useQuery(GET_JOB, {
    variables: {
      shopifyToken,
      jobId: currentJobId,
    },
  });
  const { data: templatesData } = useQuery(GET_EMAIL_TEMPLATES, { variables: { shopifyToken } });

  const { emailTemplates = [] } = { ...templatesData };
  const externalTemplates = (emailTemplates || []).filter(
    ({ group, status }) => (group === 'external' || group === 'user') && status === 'active'
  );

  const shop = propOr({}, 'shop')(subscriptionData);
  const job = propOr({}, 'job')(jobData);
  const {
    title: jobTitle = '',
    category: categoryId = '',
    location: jobLocation,
    country: jobCountry,
    description: jobDescription,
    streetAddress: jobAddress,
    teamMembers = [],
    isRemote,
    totalCandidates = {},
    status: jobStatus,
    statusUpdatedAt = new Date(),
    customQuestions = [],
    applicationFormFields = {},
    candidates = [],
    previous: previousJob,
    next: nextJob,
    visits = [],
    archived: isArchived = false,
  } = { ...job };
  const { teamMembers: shopTeamMembers = [] } = { ...shop };
  const category = categories.find(({ value }) => value === categoryId);
  const [updateJobStatus] = useMutation(UPDATE_JOB_STATUS);
  const [refreshJob] = useMutation(REFRESH_JOB);
  const [refreshJobKitSkill] = useMutation(REFRESH_JOB_KIT_SKILL);
  const [updateCandidateStatus] = useMutation(UPDATE_CANDIDATE_STATUS);
  const [createCandidateScore] = useMutation(CREATE_CANDIDATE_SCORE);
  const [updateCandidateScore] = useMutation(UPDATE_CANDIDATE_SCORE);
  const [sendCandidateEmail] = useMutation(SEND_CANDIDATE_EMAIL);
  const [archiveJob] = useMutation(ARCHIVE_JOB);
  const [unarchiveJob] = useMutation(UNARCHIVE_JOB);

  const [selected, setSelected] = useState(0);
  const [emailForm, setEmailForm] = useState({});
  const [emailModal, setEmailModal] = useState(false);
  const [tabCandidates, setTabCandidates] = useState([]);
  const [statusModal, setStatusModal] = useState(false);
  const [archiveModal, setArchiveModal] = useState(false);
  const [candidateStatusModal, setCandidateStatusModal] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [popoverShare, setPopoverShare] = useState(false);
  const [openLinkModal, setOpenLinkModal] = useState(false);
  const [sourceSelected, setSourceSelected] = useState();
  const [reason, setReason] = useState(null);
  const [emailTemplate, setEmailTemplate] = useState();

  const togglePopoverShare = useCallback(() => setPopoverShare((popoverShare) => !popoverShare), []);
  const [successToast, setSuccessToast] = useState({
    status: false,
    message: '',
  });
  const handleTabChange = useCallback((selectedTabIndex) => setSelected(selectedTabIndex), []);
  const toggleLinkModal = useCallback(() => setOpenLinkModal((openLinkModal) => !openLinkModal), []);
  const handleSourceSelectChange = useCallback((value) => setSourceSelected(value), []);

  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const shopProfile = propOr({}, 'profile')(shop);
  const dispositionCodes = propOr([], 'dispositionCodes')(shop);
  const isSubscribedUser = !isEmpty(currentSubscription);
  /*const showApplicationFormButtons =
    isSubscribedUser && (currentSubscription.subscriptionType === 'Premium' || currentSubscription.subscriptionType === 'Growth');*/
  const toggleActive = useCallback(() => setSuccessToast(({ status, message }) => ({ status: !status, message })), []);
  const toastMarkup = successToast.status ? <Toast content={successToast.message} onDismiss={toggleActive} /> : null;

  const appForm = {
    phone: 'required',
    resume: 'required',
    cover_letter: 'optional',
    newsletter_sign_up: 'optional',
    ...applicationFormFields,
  };

  useEffect(() => {
    const tab = tabs[selected];
    if (!!tab && tab.isList) {
      compose(setTabCandidates, reverse, sortBy(prop('updatedAt')))(candidates.filter(({ status }) => status === tab.id));
    }
  }, [candidates, selected]);

  useEffect(() => {
    if (!!props.match.params.tab) {
      const tabIndex = tabs.findIndex(({ id }) => id === props.match.params.tab);
      if (tabIndex && tabIndex !== selected) {
        setSelected(tabIndex);
      }
    }
  }, [props.match.params.tab]);

  // Update currentJobId when URL params change
  useEffect(() => {
    setCurrentJobId(initialJobId);
  }, [initialJobId]);

  // Reset form state when job changes
  useEffect(() => {
    setEmailForm({});
    setEmailModal(false);
    setStatusModal(false);
    setCandidateStatusModal();
    setPopoverShare(false);
    setOpenLinkModal(false);
    setSourceSelected();
    setReason(null);
    setEmailTemplate();
    setSelected(0);
  }, [currentJobId]);

  const navigateToNextJob = () => {
    if (nextJob && nextJob !== currentJobId && !jobLoading) {
      setCurrentJobId(nextJob);
    }
  };

  const navigateToPreviousJob = () => {
    if (previousJob && previousJob !== currentJobId && !jobLoading) {
      setCurrentJobId(previousJob);
    }
  };

  const triggerKitSkill = async () => {
    const response = await refreshJobKitSkill({
      variables: {
        jobId: currentJobId,
        shopifyToken,
      },
    });
    console.log(response);
  };

  /*useEffect(() => {
    if (jobStatus === 'open' && moment().diff(moment(statusUpdatedAt), 'hours') >= 30 && shop.shopifyDomain) {
      triggerKitSkill();
    }
  }, [statusUpdatedAt, shop]);*/

  const updateCandidateStatusSuccess = () => {
    setIsLoading(false);
    setSuccessToast({ status: true, message: 'The candidate status was updated successfully.' });
    setCandidateStatusModal();
    jobRefetch();
  };

  const updateCandidateStatusFailure = () => {
    setIsLoading(false);
    setSuccessToast({ status: true, message: 'There is an error with the server' });
  };

  const onUpdateCandidateStatus = () => {
    setIsLoading(true);
    const { status, candidateId } = { ...candidateStatusModal };
    updateCandidateStatus({
      variables: { candidateId, shopifyToken, status, reasonId: status === 'inactive' ? reason : null },
    })
      .then(updateCandidateStatusSuccess)
      .catch(updateCandidateStatusFailure);
  };

  const toggleEmailModal = (to, candidateId) => {
    setEmailForm({ to, candidateId });
    setEmailTemplate();
    setEmailModal(!emailModal);
  };

  const sendCandidateEmailSuccess = () => {
    setIsLoading(false);
    setSuccessToast({ status: true, message: 'The email was sent successfully.' });
    toggleEmailModal();
  };

  const onSendEmail = () => {
    setIsLoading(true);

    sendCandidateEmail({
      variables: { shopifyToken, ...emailForm },
    })
      .then(sendCandidateEmailSuccess)
      .catch(updateCandidateStatusFailure);
  };

  const onChangeEmailTemplate = (template) => {
    setEmailTemplate(template);
    const selected = externalTemplates.find(({ id }) => id === template);
    if (!!selected) {
      const { body: message, subject } = { ...selected };
      setEmailForm({ ...emailForm, subject, message });
    }
  };

  const updateStatus = async () => {
    try {
      setIsLoading(true);
      const response = await updateJobStatus({
        variables: {
          jobId: currentJobId,
          shopifyToken,
        },
      });
      const errors = pathOr([], ['data', 'updateJob', 'errors'])(response);
      const emptyErrors = isEmpty(errors);

      if (emptyErrors) {
        setStatusModal(false);
        setSuccessToast({ status: true, message: 'Job status was updated successfully.' });
      } else {
        setSuccessToast({
          status: true,
          message: 'Unexpected error updating job status, please try again',
        });
      }
    } catch (error) {
      setSuccessToast({
        status: true,
        message: 'Unexpected error updating job status, please try again',
      });
    } finally {
      setIsLoading(false);
      jobRefetch();
    }
  };

  const onRefreshJob = async () => {
    try {
      setIsLoading(true);
      const response = await refreshJob({
        variables: {
          jobId: currentJobId,
          shopifyToken,
        },
      });

      const errors = pathOr([], ['data', 'refreshJob', 'errors'])(response);
      const statusOk = pathOr(false, ['data', 'refreshJob', 'status'])(response);
      const emptyErrors = isEmpty(errors);

      if (emptyErrors && !!statusOk) {
        setSuccessToast({ status: true, message: 'Job successfully updated' });
      } else {
        setSuccessToast({
          status: true,
          message: 'Unexpected error updating job, please try again',
        });
      }
    } catch (error) {
      setSuccessToast({
        status: true,
        message: 'Unexpected error updating job, please try again',
      });
    } finally {
      setIsLoading(false);
      jobRefetch();
    }
  };

  const onArchiveJob = async () => {
    try {
      setIsLoading(true);
      const response = await archiveJob({
        variables: {
          jobId: currentJobId,
          shopifyToken,
        },
      });

      const errors = pathOr([], ['data', 'archiveJob', 'errors'])(response);
      if (isEmpty(errors)) {
        setSuccessToast({ status: true, message: 'Job archived successfully' });
      } else {
        setSuccessToast({ status: true, message: 'There is an error with the server' });
      }
    } catch (error) {
      setSuccessToast({ status: true, message: 'There is an error with the server' });
    } finally {
      setIsLoading(false);
      setArchiveModal(false);
      jobRefetch();
    }
  };

  const onUnarchiveJob = async () => {
    try {
      setIsLoading(true);
      const response = await unarchiveJob({
        variables: {
          jobId: currentJobId,
          shopifyToken,
        },
      });

      const errors = pathOr([], ['data', 'unarchiveJob', 'errors'])(response);
      if (isEmpty(errors)) {
        setSuccessToast({ status: true, message: 'Job unarchived successfully' });
      } else {
        setSuccessToast({ status: true, message: 'There is an error with the server' });
      }
    } catch (error) {
      setSuccessToast({ status: true, message: 'There is an error with the server' });
    } finally {
      setIsLoading(false);
      setArchiveModal(false);
      jobRefetch();
    }
  };

  const onCandidateScoreSuccess = () => {
    setSuccessToast({ status: true, message: 'Your review was saved successfully.' });
    jobRefetch();
  };

  const onFailure = () => {
    setSuccessToast({ status: true, message: 'There is an error with the server' });
  };

  const onChangeRating = (candidateId, isNew, overallRating) => {
    const mutation = isNew ? createCandidateScore : updateCandidateScore;
    mutation({
      variables: { candidateId, shopifyToken, thoughts: '', overallRating: getOverallRatingKey(overallRating) },
    })
      .then(onCandidateScoreSuccess)
      .catch(onFailure);
  };

  const onTwitterShare = () => {
    const { companyName } = { ...shopProfile };
    const template = `${companyName} is hiring a ${jobTitle}`;
    const url = `https://${props.prymaryUrl}/a/careers/jobs/${currentJobId}?ref=twit`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(template)}&url=${encodeURIComponent(url)}&via=ShopHire`
    );
  };

  const onLinkedinShare = () => {
    const { companyName } = { ...shopProfile };
    const url = `https://${props.prymaryUrl}/a/careers/jobs/${currentJobId}?ref=linkedin`;
    const title = `${companyName} is hiring a ${jobTitle} @ShopHire`;
    window.open(
      `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(
        title
      )}&source=${prymaryUrl}`
    );
  };

  const onFacebookShare = () => {
    const { companyName } = { ...shopProfile };
    const url = `https://${props.prymaryUrl}/a/careers/jobs/${currentJobId}?ref=linkedin`;
    const title = `${companyName} is hiring a ${jobTitle} via @ShopHire`;
    window.open(
      `https://www.facebook.com/dialog/share?app_id=${facebookAppId}&display=popup&href=${encodeURIComponent(
        url
      )}&quote=${encodeURIComponent(title)}&source=${prymaryUrl}`
    );
  };

  const statusName = (status) => {
    if ('new_candidate' === status) return 'New';
    if ('inactive' === status) return 'Disqualified';
    return `${status}`.charAt(0).toUpperCase() + `${status}`.slice(1);
  };

  const byDeviceVisits = groupBy(({ deviceType = 'Other' }) => deviceType, visits);
  const startedApplicationVisits = (visits || []).filter(({ events = [] }) => {
    return events.filter(({ name = '' }) => name === 'Application Started').length > 0;
  });
  const startedAppValue =
    startedApplicationVisits.length < totalCandidates.all ? totalCandidates.all : startedApplicationVisits.length;

  const isOpenJob = job.status === 'open';
  return (
    <AppProvider>
      <Frame>
        <Page
        breadcrumbs={[{ content: 'All jobs', onAction: () => { changeRoute('/job-list') } }]}
          title={job.title || 'Job candidates'}
          pagination={{
            hasPrevious: !jobLoading && !!previousJob && previousJob !== currentJobId,
            hasNext: !jobLoading && !!nextJob && nextJob !== currentJobId,
            onPrevious: navigateToPreviousJob,
            onNext: navigateToNextJob,
            nextTooltip: 'Next',
            previousTooltip: 'Previous',
          }}
          primaryAction={
            <Stack spacing={'loose'}>
              {!isArchived && (
                <div style={{ marginTop: '7px' }}>
                  <Button plain monochrome icon={RefreshMajorMonotone} onClick={onRefreshJob} disabled={!isOpenJob}>
                    Refresh
                  </Button>
                </div>
              )}
              {!isArchived && (
                <div style={{ marginTop: '7px' }}>
                  <Button plain monochrome icon={EditMinor} onClick={() => changeRoute(`/set-job/${currentJobId}`)}>
                    Edit
                  </Button>
                </div>
              )}
              {/*<Button plain monochrome icon={DuplicateMinor}>
                Duplicate
              </Button>*/}
              {!isArchived && (
                <div style={{ marginTop: '7px' }}>
                  <Button
                    plain
                    monochrome
                    disabled={!isOpenJob}
                    icon={ViewMinor}
                    external
                    url={`https://${props.prymaryUrl}/a/careers/jobs/${currentJobId}`}>
                    View
                  </Button>
                </div>
              )}
              {!isArchived && (
                <div style={{ marginTop: '7px' }}>
                  <Popover
                    active={popoverShare}
                    preferredAlignment={'left'}
                    activator={
                      <Button
                        onClick={togglePopoverShare}
                        plain
                        monochrome
                        disclosure
                        size={'slim'}
                        icon={PromoteMinor}
                        disabled={!isOpenJob}>
                        Share
                      </Button>
                    }
                    onClose={togglePopoverShare}>
                    <ActionList
                      items={[
                        {
                          content: 'Share on Twitter',
                          onAction: onTwitterShare,
                          icon: () => <SocialIcon source={twitterIcon} />,
                        },
                        {
                          content: 'Share on Facebook',
                          onAction: onFacebookShare,
                          icon: () => <SocialIcon source={facebookIcon} />,
                        },
                        {
                          content: 'Share on Linkedin',
                          onAction: onLinkedinShare,
                          icon: () => <SocialIcon source={linkedinIcon} />,
                        },
                        {
                          content: 'Create job link',
                          onAction: toggleLinkModal,
                          icon: LinkMinor,
                        },
                      ]}
                    />
                  </Popover>
                </div>
              )}
              <Button
                primary
                connectedDisclosure={{
                  accessibilityLabel: 'Job status',
                  actions: isArchived
                    ? [{ content: 'Unarchive', onAction: () => setArchiveModal(true) }]
                    : [
                        {
                          content: 'Open',
                          onAction: () => jobStatus !== 'open' && setStatusModal(true),
                          active: jobStatus === 'open',
                        },
                        {
                          content: 'Closed',
                          onAction: () => jobStatus === 'open' && setStatusModal(true),
                          active: jobStatus !== 'open',
                        },
                        { content: 'Archive', onAction: () => setArchiveModal(true) },
                      ],
                }}>
                {isArchived ? 'Archived' : jobStatus === 'open' ? 'Open' : 'Closed'}
              </Button>
            </Stack>
          }>
          <br />
          <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange}>
            <br />
            <Layout>
              {selected < 4 && (
                <Layout.Section>
                  {jobLoading ? (
                    <>
                      <Card sectioned>
                        <SkeletonBodyText />
                      </Card>
                      <Card sectioned>
                        <SkeletonBodyText />
                      </Card>
                    </>
                  ) : isEmpty(tabCandidates) ? (
                    <Card>
                      <Card.Section>
                        <br />
                        <br />
                        <EmptyState heading={"Sorry, you don't have any candidates"}>
                          <p>Promote your jobs on social media to drive traffic to your openings</p>
                          <br />
                          <br />
                          <div className={'empty-sharing'}>
                            <Stack vertical spacing={'tight'} alignment={'leading'}>
                              <Button plain monochrome icon={<SocialIcon source={twitterIcon} small />} onClick={onTwitterShare}>
                                Share on Twitter
                              </Button>
                              <Button
                                plain
                                monochrome
                                icon={<SocialIcon source={facebookIcon} small />}
                                onClick={onFacebookShare}>
                                Share on Facebook
                              </Button>
                              <Button
                                plain
                                monochrome
                                icon={<SocialIcon source={linkedinIcon} small />}
                                onClick={onLinkedinShare}>
                                Share on Linkedin
                              </Button>
                              <Button plain monochrome icon={<SocialIcon source={LinkMinor} small />} onClick={toggleLinkModal}>
                                Create job link
                              </Button>
                            </Stack>
                          </div>
                        </EmptyState>
                      </Card.Section>
                    </Card>
                  ) : (
                    tabCandidates.map((candidate) => {
                      const {
                        name,
                        lastName,
                        email,
                        phone,
                        status,
                        resume,
                        createdAt,
                        id: candidateId,
                        candidateScore,
                        dispositionCode = null,
                      } = {
                        ...candidate,
                      };
                      const { overallRating } = { ...candidateScore };
                      return (
                        <Card
                          key={candidateId}
                          title={
                            <Stack>
                              <span style={{ cursor: 'pointer' }} onClick={() => changeRoute(`/candidate/${candidateId}`)}>
                                <Heading>
                                  {name}
                                  {!!lastName ? ` ${lastName}` : ''}
                                </Heading>
                              </span>
                              <Badge status={statusBadgeColors[status]}>
                                <span className='capitalize'>{status === 'new_candidate' ? 'New' : status}</span>
                              </Badge>
                            </Stack>
                          }
                          actions={[
                            {
                              content: (
                                <Link url={resume} external removeUnderline>
                                  View resume
                                </Link>
                              ),
                            },
                          ]}
                          sectioned>
                          <Stack vertical>
                            <Stack>
                              <Stack.Item fill>
                                <Stack spacing={'loose'}>
                                  <Stack spacing={'extraTight'}>
                                    <div style={{ width: '12px', marginTop: '3px' }}>
                                      <Icon source={EmailMajorMonotone} color={'inkLighter'} />
                                    </div>
                                    <Button plain onClick={() => toggleEmailModal(email, candidateId)}>
                                      {email}
                                    </Button>
                                    <CopyToClipboard
                                      text={email}
                                      onCopy={() => {
                                        setSuccessToast({
                                          status: true,
                                          message: 'Copied to clipboard',
                                        });
                                      }}>
                                      <Button plain icon={DuplicateMinor} size={'slim'} />
                                    </CopyToClipboard>
                                  </Stack>
                                  <Stack spacing={'extraTight'}>
                                    <div style={{ width: '12px', marginTop: '3px' }}>
                                      <Icon source={PhoneMajorMonotone} color={'inkLighter'} />
                                    </div>
                                    <TextStyle variation='subdued'>{phone}</TextStyle>
                                    <CopyToClipboard
                                      text={phone}
                                      onCopy={() => {
                                        setSuccessToast({
                                          status: true,
                                          message: 'Copied to clipboard',
                                        });
                                      }}>
                                      <Button plain icon={DuplicateMinor} size={'slim'} />
                                    </CopyToClipboard>
                                  </Stack>
                                </Stack>
                              </Stack.Item>
                              <Stack.Item>
                                <TextStyle variation='subdued'>Applied: {moment(createdAt).format('MM/DD/YY')}</TextStyle>
                              </Stack.Item>
                            </Stack>
                            {!!dispositionCode && <TextStyle variation={'subdued'}>{dispositionCode.name}</TextStyle>}
                            <p style={{ marginBottom: '-10px' }}>Overall rating</p>
                            <Stack>
                              <Stack.Item fill>
                                <ButtonGroup segmented>
                                  {overallRatingButtons.map(({ id, name: buttonName }) => (
                                    <Button
                                      disabled={equals(overallRating, getOverallRatingKey(buttonName))}
                                      key={id}
                                      onClick={() => onChangeRating(candidateId, !candidateScore, buttonName)}>
                                      {buttonName}
                                    </Button>
                                  ))}
                                </ButtonGroup>
                              </Stack.Item>
                              <Stack.Item>
                                <Button
                                  primary
                                  connectedDisclosure={{
                                    accessibilityLabel: 'Candidate status',
                                    actions: [
                                      {
                                        content: 'Active',
                                        onAction: () =>
                                          status !== 'active' && setCandidateStatusModal({ status: 'active', candidateId }),
                                        active: status === 'active',
                                      },
                                      {
                                        content: 'Hired',
                                        onAction: () =>
                                          status !== 'hired' && setCandidateStatusModal({ status: 'hired', candidateId }),
                                        active: status === 'hired',
                                      },
                                      {
                                        content: 'Disqualify',
                                        onAction: () =>
                                          status !== 'inactive' && setCandidateStatusModal({ status: 'inactive', candidateId }),
                                        active: status === 'inactive',
                                      },
                                    ],
                                  }}>
                                  {statusName(status)}
                                </Button>
                              </Stack.Item>
                            </Stack>
                          </Stack>
                        </Card>
                      );
                    })
                  )}
                </Layout.Section>
              )}
              {selected === 4 && (
                <Layout.Section>
                  <Card sectioned>
                    <div className={'Job-Description__Section'}>
                      <SanitizedHTML allowedAttributes={false} html={jobDescription} />
                    </div>
                  </Card>
                </Layout.Section>
              )}
              {selected === 5 && (
                <Layout.Section>
                  <Card sectioned>
                    <FormLayout>
                      <TextField id='firstName' label='First Name' name='firstName' />
                      <TextField id='lastName' label='Last Name' name='lastName' />
                      <TextField id='email' label='Email' name='email' type='email' />
                      {appForm.phone !== 'disabled' && (
                        <TextField id='phone' label={`Phone ${appForm.phone === 'optional' ? '(Optional)' : ''}`} name='phone' />
                      )}

                      {appForm.address !== 'disabled' && (
                        <>
                          <br />
                          <Subheading>Address {`${appForm.address === 'optional' ? '(Optional)' : ''}`}</Subheading>
                          <br />
                          <TextField id='address' label={`Address`} name='address' />
                          <FormLayout>
                            <Stack>
                              <TextField id='city' label={`City`} name='city' />
                              <TextField id='state' label={`State`} name='state' />
                              <TextField id='zipcode' label={`Zipcode`} name='zipcode' />
                            </Stack>
                          </FormLayout>
                        </>
                      )}

                      {(appForm.linkedin_url !== 'disabled' ||
                        appForm.github_url !== 'disabled' ||
                        appForm.portfolio_url !== 'disabled' ||
                        appForm.personal_website !== 'disabled') && (
                        <>
                          <br />
                          <Subheading>Social Links {`${appForm.work_experience === 'optional' ? '(Optional)' : ''}`}</Subheading>
                          <br />
                        </>
                      )}

                      {appForm.linkedin_url !== 'disabled' && (
                        <TextField
                          id='linkedin_url'
                          label={`LinkedIn URL ${appForm.linkedin_url === 'optional' ? '(Optional)' : ''}`}
                          name='linkedin_url'
                        />
                      )}

                      {appForm.github_url !== 'disabled' && (
                        <TextField
                          id='github_url'
                          label={`Github URL ${appForm.github_url === 'optional' ? '(Optional)' : ''}`}
                          name='github_url'
                        />
                      )}

                      {appForm.portfolio_url !== 'disabled' && (
                        <TextField
                          id='portfolio_url'
                          label={`Porfolio URL ${appForm.portfolio_url === 'optional' ? '(Optional)' : ''}`}
                          name='portfolio_url'
                        />
                      )}

                      {appForm.personal_website !== 'disabled' && (
                        <TextField
                          id='personal_website'
                          label={`Personal Website ${appForm.personal_website === 'optional' ? '(Optional)' : ''}`}
                          name='personal_website'
                        />
                      )}

                      {appForm.work_experience !== 'disabled' && (
                        <>
                          <br />
                          <Subheading>
                            Work experience {`${appForm.work_experience === 'optional' ? '(Optional)' : ''}`}
                          </Subheading>
                          <br />
                          <FormLayout>
                            <TextField id='work_experience_job_title' label={'Job Title'} name='work_experience_job_title' />
                            <TextField
                              id='work_experience_job_company'
                              label={'Company Name'}
                              name='work_experience_job_company'
                            />

                            <FormLayout.Group>
                              <Select options={[{ label: 'Mar 8, 2016 - Apr 8, 2017', value: 'value' }]} />
                              <Checkbox label={'Current'} />
                            </FormLayout.Group>
                          </FormLayout>
                        </>
                      )}

                      {!!customQuestions && !!customQuestions.map && (
                        <>
                          <br />
                          <Subheading>Questions</Subheading>
                        </>
                      )}
                      {!!customQuestions &&
                        !!customQuestions.map &&
                        customQuestions.map((question) => {
                          const {
                            id: questionId,
                            type: questionType = 'text',
                            status: questionStatus,
                            question: questionLabel,
                            termsAndConditions = {},
                            options,
                          } = {
                            ...question,
                          };
                          if (questionType === 'boolean') {
                            return (
                              <div key={questionId}>
                                <p>{`${questionLabel} ${questionStatus === 'optional' ? '(Optional)' : ''}`}</p>
                                <span style={{ marginRight: '10px' }}>
                                  <RadioButton label='Yes' id={`${questionId}_yes`} name={`${questionId}_yes`} />
                                </span>
                                <RadioButton label='No' id={`${questionId}_no`} name={`${questionId}_no`} />
                              </div>
                            );
                          }
                          if (questionType === 'multiple-choice') {
                            return (
                              <div key={questionId}>
                                <p>{`${questionLabel} ${questionStatus === 'optional' ? '(Optional)' : ''}`}</p>
                                {options.map((option) => (
                                  <div key={option.id}>
                                    <RadioButton label={option.value} id={option.id} name={option.id} />
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          if (questionType === 'checkboxes') {
                            return (
                              <div key={questionId}>
                                <p>{`${questionLabel} ${questionStatus === 'optional' ? '(Optional)' : ''}`}</p>
                                {options.map((option) => (
                                  <div key={option.id}>
                                    <Checkbox label={option.value} id={option.id} name={option.id} />
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          if (questionType === 'date-picker') {
                            return (
                              <div>
                                <p style={{ marginBottom: 5 }}>{`${questionLabel} ${
                                  questionStatus === 'optional' ? '(Optional)' : ''
                                }`}</p>
                                <div key={questionId} style={{ display: 'flex' }}>
                                  <div style={{ width: 100, marginRight: 10 }}>
                                    <TextField id={questionId} name={questionId} placeholder={'12'} />
                                  </div>
                                  <div style={{ width: 100, marginRight: 10 }}>
                                    <Select
                                      options={[
                                        { label: 'October', value: 'october' },
                                        { label: 'November', value: 'november' },
                                        { label: 'December', value: 'december' },
                                      ]}
                                      value={''}
                                    />
                                  </div>
                                  <div style={{ width: 100 }}>
                                    <Select
                                      options={[
                                        { label: '2019', value: '2019' },
                                        { label: '2020', value: '2020' },
                                        { label: '2021', value: '2021' },
                                      ]}
                                      value={''}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          if (questionType === 'terms-and-conditions') {
                            return (
                              <div>
                                <p>{`${termsAndConditions.disclaimerText} ${
                                  questionStatus === 'optional' ? '(Optional)' : ''
                                }`}</p>
                                <Checkbox label={termsAndConditions.checkboxLabelText} id={questionId} name={questionId} />
                              </div>
                            );
                          }
                          return (
                            <TextField
                              key={questionId}
                              id={questionId}
                              label={`${questionLabel} ${questionStatus === 'optional' ? '(Optional)' : ''}`}
                              name={questionId}
                              multiline={questionType === 'paragraph' ? 4 : null}
                            />
                          );
                        })}

                      {(appForm.resume !== 'disabled' || appForm.cover_letter !== 'disabled') && (
                        <>
                          <br />
                          <Subheading>Attachments</Subheading>
                        </>
                      )}
                      {appForm.resume !== 'disabled' && (
                        <DropZone
                          allowMultiple={false}
                          label={`Resume ${appForm.resume === 'optional' ? '(Optional)' : ''}`}
                          id='resume'>
                          <DropZone.FileUpload actionTitle='Add resume' actionHint='or drop an file to upload' />
                        </DropZone>
                      )}
                      {appForm.newsletter_sign_up !== 'disabled' && <Checkbox label='Join Our news letter to learn more' />}
                      {appForm.cover_letter !== 'disabled' && (
                        <TextField
                          id='coverLetter'
                          label={`Cover Letter ${appForm.cover_letter === 'optional' ? '(Optional)' : ''}`}
                          name='coverLetter'
                          multiline={3}
                        />
                      )}
                      <Button primary>Submit Application</Button>
                    </FormLayout>
                  </Card>
                </Layout.Section>
              )}
              <Layout.Section secondary>
                <Card
                  title={'Job'}
                  actions={isArchived ? [] : [{ content: 'Edit job', onAction: () => changeRoute(`/set-job/${currentJobId}`) }]}>
                  <Card.Section>
                    {jobLoading ? (
                      <SkeletonBodyText />
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <TextStyle>{jobTitle}</TextStyle>
                        <TextStyle>{{ ...category }.label}</TextStyle>
                        <span style={{ marginTop: '5px' }}>
                          <TextStyle variation='subdued'>{`${isArchived ? 'Archived on' : jobStatus === 'open' ? 'Opened on' : 'Closed on'} ${moment(
                            statusUpdatedAt
                          ).format('MMMM D, YYYY')}`}</TextStyle>
                        </span>
                      </div>
                    )}
                  </Card.Section>
                  <Card.Section>
                    <Stack>
                      <Stack.Item fill>
                        <Subheading>Candidates</Subheading>
                      </Stack.Item>
                      <Stack.Item>
                        <TextStyle variation='subdued'>{totalCandidates.all || 0}</TextStyle>
                      </Stack.Item>
                    </Stack>
                    <Stack>
                      <Stack.Item fill>New</Stack.Item>
                      <Stack.Item>{totalCandidates.new_candidate || 0}</Stack.Item>
                    </Stack>
                    <Stack>
                      <Stack.Item fill>Active</Stack.Item>
                      <Stack.Item>{totalCandidates.active || 0}</Stack.Item>
                    </Stack>
                    <Stack>
                      <Stack.Item fill>Hired</Stack.Item>
                      <Stack.Item>{totalCandidates.hired || 0}</Stack.Item>
                    </Stack>
                    <Stack>
                      <Stack.Item fill>Inactive</Stack.Item>
                      <Stack.Item>{totalCandidates.inactive || 0}</Stack.Item>
                    </Stack>
                  </Card.Section>
                  <Card.Section title={'Location'}>
                    {jobLoading ? (
                      <SkeletonBodyText />
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <p>{isRemote ? 'Remote' : jobAddress}</p>
                        {!isRemote && <p>{jobLocation}</p>}
                        {!isRemote && <p>{jobCountry}</p>}
                      </div>
                    )}
                  </Card.Section>
                  {!isEmpty(teamMembers) && (
                    <Card.Section title='Hiring team'>
                      <Stack spacing={'tight'}>
                        {teamMembers.map((member) => {
                          const { firstName, lastName, email } = { ...member };
                          return (
                            <TextContainer>
                              <p>
                                {firstName} {lastName}
                              </p>
                              <TextStyle variation='subdued'>{email}</TextStyle>
                            </TextContainer>
                          );
                        })}
                      </Stack>
                    </Card.Section>
                  )}
                  {(visits || []).length >= 10 && totalCandidates.all > 0 && (
                    <>
                      <Card.Section title='Conversion funnel'>
                        <table>
                          <tr>
                            <td style={{ width: '65%' }}>Viewed job</td>
                            <td style={{ textAlign: 'right', width: '15%' }}>{visits.length}</td>
                            <td style={{ textAlign: 'right', width: '5%' }}> </td>
                            <td style={{ textAlign: 'right', width: '15%' }}></td>
                          </tr>
                          <tr>
                            <td style={{ width: '65%' }}>Start application</td>
                            <td style={{ textAlign: 'right', width: '15%' }}>{startedAppValue}</td>
                            <td style={{ textAlign: 'right', width: '5%' }}> </td>
                            <td style={{ textAlign: 'right', width: '15%' }}>
                              {Number((startedAppValue / visits.length) * 100).toFixed(2)}%
                            </td>
                          </tr>
                          <tr>
                            <td style={{ width: '65%' }}>Applied</td>
                            <td style={{ textAlign: 'right', width: '15%' }}>{totalCandidates.all}</td>
                            <td style={{ textAlign: 'right', width: '5%' }}> </td>
                            <td style={{ textAlign: 'right', width: '15%' }}>
                              {Number((totalCandidates.all / visits.length) * 100).toFixed(2)}%
                            </td>
                          </tr>
                        </table>
                      </Card.Section>
                      <Card.Section title='Pageviews by device'>
                        <table>
                          {Object.keys(byDeviceVisits).map((el) => (
                            <tr>
                              <td style={{ width: '65%' }}>{el}</td>
                              <td style={{ textAlign: 'right', width: '15%' }}>{byDeviceVisits[el].length}</td>
                              <td style={{ textAlign: 'right', width: '5%' }}> </td>
                              <td style={{ textAlign: 'right', width: '15%' }}>
                                {Number((byDeviceVisits[el].length / visits.length) * 100).toFixed(2)}%
                              </td>
                            </tr>
                          ))}
                        </table>
                      </Card.Section>
                    </>
                  )}
                </Card>
              </Layout.Section>
            </Layout>
          </Tabs>
          {toastMarkup}
          <Modal
            open={statusModal}
            primaryAction={{
              content: `Yes, ${jobStatus == 'open' ? 'close' : 'open'} the job`,
              onAction: () => updateStatus(),
              disabled: isLoading,
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: () => setStatusModal(false),
                disabled: isLoading,
              },
            ]}
            loading={false}>
            <Modal.Section>
              {isLoading ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100vh',
                  }}>
                  <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
                </div>
              ) : (
                <TextContainer>
                  <Heading>{`Are you sure you want to ${jobStatus == 'open' ? 'close' : 'open'} this job?`}</Heading>
                  <p> Job Title: {jobTitle} </p>
                  <p className='capitalize'> Category: {{ ...category }.label} </p>
                  <p> Location: {isRemote ? 'Remote' : jobLocation} </p>
                </TextContainer>
              )}
            </Modal.Section>
          </Modal>
          <Modal
            open={archiveModal}
            onClose={() => setArchiveModal(false)}
            primaryAction={{
              content: isArchived ? 'Yes, unarchive the job' : 'Yes, archive the job',
              onAction: isArchived ? onUnarchiveJob : onArchiveJob,
              disabled: isLoading,
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: () => setArchiveModal(false),
                disabled: isLoading,
              },
            ]}
            loading={isLoading}>
            <Modal.Section>
              <TextContainer>
                <Heading>{`Are you sure you want to ${isArchived ? 'unarchive' : 'archive'} this job?`}</Heading>
                <p>Job Title: {jobTitle}</p>
                <p className='capitalize'>Category: {{ ...category }.label}</p>
                <p>Location: {isRemote ? 'Remote' : jobLocation}</p>
                {!isArchived && (
                  <p style={{ marginTop: '10px', color: '#637381' }}>
                    Note: Archiving a job will close it and hide it from your career portal. You can unarchive it later.
                  </p>
                )}
              </TextContainer>
            </Modal.Section>
          </Modal>
          <Modal
            open={!!candidateStatusModal}
            onClose={() => setCandidateStatusModal()}
            title={(candidateStatusModal || {}).status === 'inactive' ? 'Disqualification reason' : null}
            primaryAction={{
              content: (candidateStatusModal || {}).status === 'inactive' ? 'Save' : 'Yes',
              onAction: onUpdateCandidateStatus,
              disabled: isLoading || ((candidateStatusModal || {}).status === 'inactive' && !reason),
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: () => setCandidateStatusModal(),
                disabled: isLoading,
              },
            ]}
            loading={false}>
            <Modal.Section>
              {(candidateStatusModal || {}).status !== 'inactive' && (
                <>
                  <p>Are you sure you want to change this candidate status?</p>
                  {isLoading && (
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}>
                      <Loader type='TailSpin' color='#6371c7' height={40} width={40} />
                    </div>
                  )}
                </>
              )}
              {(candidateStatusModal || {}).status === 'inactive' && (
                <>
                  <p>Why are you disqualifying selected candidate from the {jobTitle} position?</p>
                  <br />
                  <Select
                    labelHidden={true}
                    options={dispositionCodes.map(({ id: value, name: label }) => ({ value, label }))}
                    onChange={setReason}
                    placeholder={'Select a disqualification reason'}
                    value={reason}
                  />
                </>
              )}
            </Modal.Section>
          </Modal>
          <Modal
            open={openLinkModal}
            onClose={toggleLinkModal}
            title={'Create job link'}
            secondaryActions={[
              {
                content: 'Close',
                onAction: toggleLinkModal,
                disabled: isLoading,
              },
            ]}
            loading={false}>
            <Modal.Section>
              <Subheading>Option 1: No source tracking</Subheading>
              <p>The source of any candidate applying through this link will default to "Career Portal"</p>
              <br />
              <CopyToClipboard
                text={`https://${prymaryUrl}/a/careers/jobs/${currentJobId}`}
                onCopy={() => {
                  setSuccessToast({
                    status: true,
                    message: 'Copied to clipboard',
                  });
                }}>
                <Button
                  size={'slim'}
                  fullWidth
                  icon={DuplicateMinor}
                  textAlign={'left'}>{`https://${prymaryUrl}/a/careers/jobs/${currentJobId}`}</Button>
              </CopyToClipboard>
            </Modal.Section>
            <Modal.Section>
              <Subheading>Option 2: Get job board link</Subheading>
              <p style={{ marginBottom: '10px' }}>
                The source of any candidate applying through this link will default to the job board selected
              </p>
              <Select
                label={'Job boards'}
                placeholder={'Select'}
                options={SOURCE_OPTIONS}
                onChange={handleSourceSelectChange}
                value={sourceSelected}
              />
              {!!sourceSelected && (
                <>
                  <br />
                  <CopyToClipboard
                    text={`https://${prymaryUrl}/a/careers/jobs/${currentJobId}?ref=${sourceSelected}`}
                    onCopy={() => {
                      setSuccessToast({
                        status: true,
                        message: 'Copied to clipboard',
                      });
                    }}>
                    <Button
                      size={'slim'}
                      fullWidth
                      icon={DuplicateMinor}
                      textAlign={
                        'left'
                      }>{`https://${prymaryUrl}/a/careers/jobs/${currentJobId}?ref=${sourceSelected}`}</Button>
                  </CopyToClipboard>
                </>
              )}
            </Modal.Section>
          </Modal>
          <Modal
            open={!!emailModal}
            onClose={toggleEmailModal}
            title={'Contact candidate'}
            primaryAction={{
              content: 'Send Message',
              onAction: onSendEmail,
              disabled: !(!!emailForm?.to && !!emailForm?.from && !!emailForm?.subject && !!emailForm?.message),
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: toggleEmailModal,
              },
            ]}
            loading={false}>
            <Modal.Section>
              <FormLayout>
                <Select
                  label='Email template'
                  options={externalTemplates.map((template) => {
                    const { id, title } = { ...template };
                    return {
                      value: id,
                      label: title,
                    };
                  })}
                  value={emailTemplate}
                  onChange={onChangeEmailTemplate}
                  placeholder={'Select email template'}
                />
                <FormLayout.Group>
                  <TextField
                    label='To'
                    value={emailForm?.to}
                    disabled={true}
                    onChange={(to) => setEmailForm({ ...emailForm, to })}
                  />
                  <Select
                    label='From'
                    options={(shopTeamMembers || []).map((member) => {
                      const { firstName, lastName, email } = { ...member };
                      return {
                        value: email,
                        label: `${firstName} ${lastName} <${email}>`,
                      };
                    })}
                    onChange={(from) => setEmailForm({ ...emailForm, from })}
                    value={emailForm?.from}
                    placeholder={'Select a hiring team member'}
                  />
                </FormLayout.Group>
                <TextField
                  label='Subject'
                  value={emailForm?.subject}
                  onChange={(subject) => setEmailForm({ ...emailForm, subject })}
                />
                <Editor
                  value={emailForm?.message}
                  initialValue={emailForm?.message || ''}
                  init={{
                    height: 310,
                    menubar: false,
                    plugins: ['advlist autolink lists link', 'code fullscreen', 'wordcount'],
                    toolbar:
                      'undo redo | formatselect | fontselect | fontsizeselect | bold italic forecolor | \
                        alignleft aligncenter alignright alignjustify | \
                        bullist numlist outdent indent | removeformat | help',
                  }}
                  id='body'
                  name='body'
                  apiKey='czdl8uo4qtlzlp7ozdpws89rify9vokuyuvw21w1jr8n394z'
                  onEditorChange={(message) => setEmailForm({ ...emailForm, message })}
                />
              </FormLayout>
            </Modal.Section>
          </Modal>
        </Page>
        <Footer link='https://help.shophire.co/article/6-jobs' name='Jobs' />
      </Frame>
    </AppProvider>
  );
};

export default JobCandidates;
